import { Plus } from "lucide-react";
import { ShiftCard } from "./ShiftCard";
import { isoDate } from "./shiftUtils";

// First column (#, site/client) is sticky
const STICKY_HEADER_CLASS = "sticky left-0 z-30 bg-[#0A0A0A]";
const STICKY_CELL_CLASS = "sticky left-0 z-20 bg-[#0A0A0A]";

export function RosterGrid({
  weeks,
  days,
  visibleSites,
  totalSitesCount,
  cellMap,
  conflictIds,
  vacationConflictIds,
  shortRest,
  onCellEmptyClick,
  onAddToCell,
  onDropMove,
  onEditShift,
  onEditSeries,
  onDeleteSeries,
  onDeleteShift,
  maxHeight = "calc(100vh - 280px)",
}) {
  const gridCols = `40px 220px repeat(${days.length}, minmax(${weeks === 4 ? 90 : 120}px, 1fr))`;
  const todayKey = new Date().toDateString();
  return (
    <div
      className="border border-[#27272A] bg-[#121212] overflow-auto"
      style={{ maxHeight }}
      data-testid="roster-grid"
    >
      <div className="min-w-[1100px]">
        {/* Header */}
        <div
          className="grid border-b border-[#27272A] bg-[#0A0A0A] sticky top-0 z-30"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div className={`${STICKY_HEADER_CLASS} px-2 py-3 border-r border-[#27272A] text-center`} style={{ left: 0 }}>
            <div className="label-mute">#</div>
          </div>
          <div className={`${STICKY_HEADER_CLASS} px-4 py-3 border-r border-[#27272A]`} style={{ left: 40 }}>
            <div className="label-mute">Site</div>
          </div>
          {days.map((d) => {
            const isToday = d.toDateString() === todayKey;
            return (
              <div
                key={d.toISOString()}
                data-testid={`roster-header-day-${isoDate(d)}${isToday ? "-today" : ""}`}
                className={`px-3 py-3 last:border-r-0 relative ${
                  isToday
                    ? "border-l-2 border-r-2 border-dashed border-[#FF3B30]"
                    : "border-r border-[#27272A]"
                }`}
              >
                <div className={`label-mute inline-flex items-center gap-1.5 ${isToday ? "text-[#FF3B30]" : ""}`}>
                  {isToday && (
                    <span
                      data-testid="today-header-pill"
                      className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#FF3B30] text-white"
                    >
                      Today
                    </span>
                  )}
                  {d.toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <div className={`font-mono font-bold text-base ${isToday ? "text-[#FF3B30]" : ""}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {visibleSites.length === 0 && (
          <div className="p-12 text-center text-zinc-500 scanlines">
            {totalSitesCount === 0 ? "No sites configured." : "No sites match the current filters."}
          </div>
        )}

        {visibleSites.map((site, rowIdx) => (
          <div
            key={site.id}
            className="grid border-b last:border-b-0 border-[#27272A]"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div className={`${STICKY_CELL_CLASS} px-2 py-3 border-r border-[#27272A] text-center font-mono text-sm text-zinc-500`} style={{ left: 0 }}>
              {rowIdx + 1}
            </div>
            <div className={`${STICKY_CELL_CLASS} px-4 py-3 border-r border-[#27272A]`} style={{ left: 40 }}>
              <div className="label-mute truncate">{site.client_name}</div>
              <div className="flex items-center gap-2 min-w-0">
                <div className="font-display text-base uppercase font-bold tracking-tight truncate">{site.name}</div>
                {(cellMap[`${site.id}|${todayKey}`] || []).length > 0 && (
                  <span
                    data-testid={`roster-today-pill-${site.id}`}
                    className="shrink-0 text-[9px] font-mono uppercase tracking-widest bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 px-1.5 py-0.5 rounded-sm"
                    title="Site has a shift today"
                  >
                    Today
                  </span>
                )}
              </div>
            </div>
            {days.map((d) => {
              const cell = cellMap[`${site.id}|${d.toDateString()}`] || [];
              const dateStr = isoDate(d);
              const isToday = d.toDateString() === todayKey;
              return (
                <div
                  key={d.toISOString()}
                  data-testid={`cell-${site.id}-${dateStr}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDropMove(e, site.id, dateStr)}
                  onClick={(e) => {
                    if (cell.length === 0 && e.target === e.currentTarget) {
                      onCellEmptyClick(site.id, d);
                    }
                  }}
                  // iter322 — Today's column is framed with dashed red
                  // L+R borders (mirroring the "+ Add" hover language)
                  // so it reads as a distinct "lane" without needing a
                  // background tint or stripes.
                  className={`p-1.5 min-h-[100px] space-y-1.5 ${
                    isToday
                      ? "border-l-2 border-r-2 border-dashed border-[#FF3B30] last:border-r-2"
                      : "border-r last:border-r-0 border-[#27272A]"
                  } ${cell.length === 0 ? "cursor-pointer hover:bg-[#FF3B30]/5" : ""}`}
                >
                  {cell.length === 0 && (
                    <div className="h-full w-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                      <Plus className="size-4 text-[#FF3B30]" />
                    </div>
                  )}
                  {cell.map((s) => (
                    <ShiftCard
                      key={s.id}
                      shift={s}
                      isConflict={conflictIds.has(s.id)}
                      isVacationConflict={vacationConflictIds && vacationConflictIds.has(s.id)}
                      shortRestMinutes={shortRest ? shortRest.get(s.id) : undefined}
                      onEdit={onEditShift}
                      onEditSeries={onEditSeries}
                      onDeleteSeries={onDeleteSeries}
                      onDelete={onDeleteShift}
                      density={weeks >= 4 ? "compact" : weeks >= 2 ? "medium" : "roomy"}
                    />
                  ))}
                  {cell.length > 0 && (
                    <button
                      data-testid={`add-to-cell-${site.id}-${dateStr}`}
                      onClick={(e) => { e.stopPropagation(); onAddToCell(site.id, d); }}
                      title="Add another shift to this cell"
                      className="w-full border border-dashed border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-600 text-[10px] uppercase tracking-widest py-1 transition-colors duration-150 flex items-center justify-center gap-1"
                    >
                      <Plus className="size-3" /> Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
